import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Target, Eye, Award } from "lucide-react";

const values = [
  {
    icon: ShieldCheck,
    title: "Integrity First",
    text: "We handle your most sensitive systems and data with absolute discretion and honesty. Trust is the foundation of every engagement.",
  },
  {
    icon: Target,
    title: "Precision & Depth",
    text: "We go beyond surface-level scans. Our experts dig deep to uncover the root causes of risk and deliver clear, actionable remediation.",
  },
  {
    icon: Eye,
    title: "Always Watching",
    text: "Threats never sleep, and neither do we. Round-the-clock monitoring and rapid response keep your business protected 24/7.",
  },
  {
    icon: Award,
    title: "Proven Expertise",
    text: "Our team holds industry-leading certifications and has defended reputable companies and government agencies alike.",
  },
];

export default function About() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Header */}
      <header className="border-b border-slate-800/60 bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="about-home-link">
            <img src="/maktran-mark.png" alt="Maktran Cyber logo" className="h-9 w-auto mr-3" />
            <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Maktran Cyber
            </span>
          </Link>
          <Link
            to="/"
            className="flex items-center text-slate-300 hover:text-white transition-colors text-sm"
            data-testid="about-back-link"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="flex-1" data-testid="about-page">
        {/* Intro */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-red-500 bg-clip-text text-transparent">
                About Maktran Cyber
              </span>
            </h1>
            <p className="text-lg text-slate-300 leading-relaxed mb-4">
              Maktran Cyber is an elite cybersecurity consultancy dedicated to protecting
              businesses from an ever-evolving threat landscape. We combine deep technical
              expertise with a relentless commitment to our clients' security.
            </p>
            <p className="text-slate-400 leading-relaxed">
              From digital forensics and incident response to penetration testing and
              security analysis, we deliver the clarity and confidence organizations need to
              operate safely in a connected world.
            </p>
          </motion.div>
        </section>

        {/* Mission / Vision */}
        <section className="bg-slate-800/30 border-y border-slate-800">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid md:grid-cols-2 gap-10">
            <motion.div
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-3">
                <Target className="h-6 w-6 text-blue-400 mr-3" />
                <h2 className="text-2xl font-bold">Our Mission</h2>
              </div>
              <p className="text-slate-300 leading-relaxed">
                To empower organizations to defend their digital fortress — making
                world-class security accessible, proactive, and human. We turn complex risk
                into clear action so our clients can focus on what they do best.
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 24 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center mb-3">
                <Eye className="h-6 w-6 text-red-400 mr-3" />
                <h2 className="text-2xl font-bold">Our Vision</h2>
              </div>
              <p className="text-slate-300 leading-relaxed">
                A world where every business — regardless of size — can operate securely and
                resiliently. We strive to be the trusted partner organizations turn to before,
                during, and after a security incident.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Values */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-3xl font-bold mb-10"
          >
            What We Stand For
          </motion.h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 hover:border-blue-500/50 transition-colors"
                  data-testid={`about-value-${i}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-slate-900/60 border border-slate-700 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{v.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{v.text}</p>
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-gradient-to-r from-blue-900/40 to-slate-800/40 border border-slate-700/50 rounded-2xl p-8 sm:p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to secure your business?</h2>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Talk to our team about a comprehensive security assessment tailored to your
              organization.
            </p>
            <Link
              to="/#contact"
              data-testid="about-cta"
              className="inline-flex items-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              Get in Touch
            </Link>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-800 py-8 text-center text-slate-500 text-sm">
        &copy; 2026 Maktran Cyber. All rights reserved.
      </footer>
    </div>
  );
}
